import React from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "components/ui/dialog";
import { formatReviewOfferCopy, getReviewOfferCopy } from "./reviewOfferCopy";
import { DEFAULT_REVIEW_OFFER_PERCENT, DEFAULT_REVIEW_OFFER_PROGRAM, ReviewOfferProgram } from "./types";
import { DiscountSwapDirection } from "./useDiscountExclusivity";

export type DiscountSwapDialogProps = {
  /** null => closed. Which title/copy direction to show (S6, frontend-spec.md) */
  direction: DiscountSwapDirection | null;
  /**
   * HUB-466: which program's copy/percent to show — the entitlement being
   * swapped away from/to. Defaults to CHECKOUT_5 (phase-1 behavior) so
   * existing callers that don't pass an entitlement keep working unchanged.
   */
  program?: ReviewOfferProgram;
  /** The entitlement's OWN discount_percent — never a hardcoded "5%". Defaults to 5 (phase-1 behavior). */
  percent?: number;
  isSubmitting?: boolean;
  /** the last confirm attempt failed server-side (Greptile #3) — stays open, shows an error, "Switch" becomes "Retry" */
  hasError?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * The "one discount per order" swap confirmation (frontend-spec.md S6).
 * 430px modal, built on the repo's existing Radix dialog primitive
 * (components/ui/dialog.tsx) — no new modal primitive introduced.
 * Reused for BOTH swap directions (reviewToCoupon / couponToReview); only
 * the title copy differs (copy.offer.swap.title / .reverse).
 */
export const DiscountSwapDialog = ({
  direction,
  program = DEFAULT_REVIEW_OFFER_PROGRAM,
  percent = DEFAULT_REVIEW_OFFER_PERCENT,
  isSubmitting,
  hasError,
  onCancel,
  onConfirm,
}: DiscountSwapDialogProps) => {
  const copy = getReviewOfferCopy(program);
  const title = formatReviewOfferCopy(
    direction === "couponToReview" ? copy.offer.swap.reverse : copy.offer.swap.title,
    percent
  );

  return (
    <Dialog open={direction !== null} onOpenChange={(open) => !open && onCancel()}>
      {/* z-[1400]: this dialog opens from inside the order-details drawer
          (a Sheet at z-[1300]); without raising both overlay and content it
          renders behind the drawer's dim backdrop. Matches the review widget
          popup's z-[1400]. */}
      <DialogContent
        className="z-[1400] max-w-[430px]"
        overlayClassName="z-[1400]"
        data-cypress-id="discountSwapDialog"
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{copy.offer.swap.body}</DialogDescription>
        {hasError && (
          <p className="text-sm text-destructive" data-cypress-id="discountSwapError">
            {copy.offer.swap.error}
          </p>
        )}
        <DialogFooter>
          <button
            type="button"
            onClick={onCancel}
            className="box-border flex h-9 shrink-0 items-center justify-center rounded-lg border border-solid border-border bg-background px-4 text-sm font-medium text-foreground shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] hover:bg-slate-50 dark:hover:bg-accent"
            data-cypress-id="discountSwapKeepButton"
          >
            {copy.offer.swap.keep}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting}
            className="box-border flex h-9 shrink-0 items-center justify-center rounded-lg bg-[#1A2547] px-4 text-sm font-medium text-white shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] hover:bg-[#1A2547]/90 disabled:opacity-50"
            data-cypress-id="discountSwapSwitchButton"
          >
            {hasError ? copy.offer.swap.retry : copy.offer.swap.switch}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
